import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ScheduledPostWithPrompts {
  id: string;
  post_content: string;
  channel: string;
  status: string;
  created_at: string;
  prompts: {
    id: string;
    name: string;
    prompt: string;
    prompt_type: string;
  }[];
}

export const useScheduledPostsWithPrompts = (brandId: string | null) => {
  const [posts, setPosts] = useState<ScheduledPostWithPrompts[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPostsWithPrompts = async () => {
    if (!brandId) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    try {
      // First get scheduled posts
      const { data: scheduledPosts, error: postsError } = await supabase
        .from('cm_posts_scheduled')
        .select('id, post_content, channel, status, created_at')
        .eq('brand_id', brandId)
        .eq('status', 'scheduled')
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      // Then get feature_image prompts only
      const { data: prompts, error: promptsError } = await supabase
        .from('cm_prompts')
        .select('id, name, prompt, prompt_type')
        .eq('brand_id', brandId)
        .eq('prompt_type', 'feature_image')
        .order('created_at', { ascending: false });

      if (promptsError) throw promptsError;

      // Combine the data - each post gets all available feature_image prompts
      const postsWithPrompts = (scheduledPosts || []).map(post => ({
        ...post,
        prompts: prompts || []
      }));

      setPosts(postsWithPrompts);
    } catch (error) {
      console.error('Error fetching scheduled posts with prompts:', error);
      toast.error('Failed to load scheduled posts');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePromptWithAI = async (postContent: string) => {
    try {
      const response = await supabase.functions.invoke('generate-feature-image-prompt', {
        body: { 
          postContent,
          brandId
        }
      });

      if (response.error) throw response.error;
      
      return response.data?.prompt || null;
    } catch (error) {
      console.error('Error generating prompt with AI:', error);
      toast.error('Failed to generate prompt with AI');
      return null;
    }
  };

  useEffect(() => {
    fetchPostsWithPrompts();
  }, [brandId]);

  return {
    posts,
    isLoading,
    refetch: fetchPostsWithPrompts,
    generatePromptWithAI
  };
};